import { 
  Directive, 
  Input, 
  TemplateRef, 
  ViewContainerRef, 
  OnInit, 
  OnDestroy 
} from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthorizationService } from '../services/authorization.service';

/**
 * Directiva estructural para mostrar/ocultar elementos según permisos.
 * 
 * Uso:
 * - Un permiso: *appHasPermission="'PROJECTS_CREATE'"
 * - Varios permisos (OR): *appHasPermission="['PROJECTS_CREATE', 'PROJECTS_UPDATE']"
 * - Negación: *appHasPermission="'PROJECTS_DELETE'; else noPermission"
 */
@Directive({
  selector: '[appHasPermission]'
})
export class HasPermissionDirective implements OnInit, OnDestroy {
  private permissions: string | string[] = [];
  private subscription?: Subscription;
  private isVisible = false;

  @Input() set appHasPermission(permissions: string | string[]) {
    this.permissions = permissions;
    this.updateView();
  }

  @Input() appHasPermissionElse?: TemplateRef<any>;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private authService: AuthorizationService
  ) {}

  ngOnInit(): void {
    // Suscribirse a cambios en los permisos
    this.subscription = this.authService.permissions$.subscribe(() => {
      this.updateView();
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private updateView(): void {
    const hasPermission = this.checkPermission();

    if (hasPermission && !this.isVisible) {
      // Mostrar el elemento
      this.viewContainer.clear();
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.isVisible = true;
    } else if (!hasPermission && this.isVisible) {
      // Ocultar el elemento
      this.viewContainer.clear();
      if (this.appHasPermissionElse) {
        this.viewContainer.createEmbeddedView(this.appHasPermissionElse);
      }
      this.isVisible = false;
    } else if (!hasPermission && !this.isVisible && this.appHasPermissionElse) {
      // Mostrar template alternativo si no tiene permiso
      this.viewContainer.clear();
      this.viewContainer.createEmbeddedView(this.appHasPermissionElse);
    }
  }

  private checkPermission(): boolean {
    if (!this.permissions || (Array.isArray(this.permissions) && this.permissions.length === 0)) {
      return true; // Sin restricciones
    }

    if (Array.isArray(this.permissions)) {
      return this.authService.hasAnyPermission(this.permissions);
    }

    return this.authService.hasPermission(this.permissions);
  }
}

/**
 * Directiva para verificar roles
 */
@Directive({
  selector: '[appHasRole]'
})
export class HasRoleDirective implements OnInit, OnDestroy {
  private roles: string | string[] = [];
  private subscription?: Subscription;
  private isVisible = false;

  @Input() set appHasRole(roles: string | string[]) {
    this.roles = roles;
    this.updateView();
  }

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private authService: AuthorizationService
  ) {}

  ngOnInit(): void {
    this.subscription = this.authService.roles$.subscribe(() => {
      this.updateView();
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private updateView(): void {
    const hasRole = this.checkRole();

    if (hasRole && !this.isVisible) {
      this.viewContainer.clear();
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.isVisible = true;
    } else if (!hasRole && this.isVisible) {
      this.viewContainer.clear();
      this.isVisible = false;
    }
  }

  private checkRole(): boolean {
    if (!this.roles || (Array.isArray(this.roles) && this.roles.length === 0)) {
      return true;
    }

    if (Array.isArray(this.roles)) {
      const userRoles = this.authService.getAllRoles();
      return this.roles.some(r => userRoles.includes(r));
    }

    return this.authService.hasRole(this.roles);
  }
}

